import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LoginModal from './LoginModal';
import { 
  Package, 
  Calendar,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ShoppingBag,
  Copy,
  ExternalLink,
  ArrowLeft
} from 'lucide-react';
import { fetchOrderHistory } from '../api/orderApi';

export default function OrderHistoryPage() {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 获取登录状态
  const { isLoggedIn, user } = useSelector((state) => state.user);
  
  // 判断是否在侧边栏版本
  const isSidebarVersion = location.pathname === '/orders-sidebar';

  // 控制登录弹窗显示
  useEffect(() => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      setLoading(false);
    } else {
      setShowLoginModal(false);
    }
  }, [isLoggedIn]);

  // 获取真实订单数据
  useEffect(() => {
    // 如果用户未登录，不获取订单数据
    if (!isLoggedIn) {
      return;
    }

    const loadOrderHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 从 localStorage 获取用户信息
        const userInfoStr = localStorage.getItem('userInfo');
        if (!userInfoStr) {
          throw new Error(t('orderHistory.userNotLoggedIn'));
        }
        
        const userInfo = JSON.parse(userInfoStr);
        const user_key = userInfo.user_key || userInfo.uid;
        
        if (!user_key) {
          throw new Error(t('orderHistory.cannotGetUserId'));
        }
        
        // 调用 API 获取订单历史
        const response = await fetchOrderHistory({ user_key: String(user_key) });
        
        if (response.data && response.data.get_order_history) {
          // 打印原始数据用于调试
          console.log('原始订单数据:', response.data.get_order_history);
          
          // 根据产品名称推断产品类型
          const getProductType = (productName) => {
            if (!productName) return 'default';
            
            const name = productName.toLowerCase();
            if (name.includes('gpt') || name.includes('chatgpt')) {
              return 'gpt';
            } else if (name.includes('claude')) {
              return 'claude';
            } else if (name.includes('sora')) {
              return 'sora';
            } else if (name.includes('midjourney')) {
              return 'midjourney';
            } else if (name.includes('grok')) {
              return 'grok';
            } else if (name.includes('大会员')) {
              return 'vip';
            } else if (name.includes('netflix')) {
              return 'netflix';
            } else if (name.includes('spotify')) {
              return 'spotify';
            } else if (name.includes('youtube')) {
              return 'youtube';
            } else if (name.includes('office')) {
              return 'office';
            } else if (name.includes('adobe')) {
              return 'adobe';
            } else if (name.includes('紫刀币')) {
              return 'coin';
            }
            // 处理旧版本产品名称
            else if (name.includes('4.0')) {
              return 'gpt';
            }
            return 'default';
          };
          
          // 转换数据格式
          const transformedOrders = response.data.get_order_history.map((order, idx) => {
            // 调试：打印每个订单的价格信息
            console.log(`订单 ${idx + 1} 价格信息:`, {
              product_price_cny: order.product_price_cny,
              product_price_usd: order.product_price_usd,
              parsed_usd: parseFloat(order.product_price_usd || '0')
            });
            
            // 调试：打印产品类型判断
            const productType = getProductType(order.product_name);
            console.log(`订单 ${idx + 1} 产品类型:`, {
              product_name: order.product_name,
              product_type: productType,
              image_path: `/images/${productType}-main.jpg`
            });
            
            return {
              id: order.orderNo || order.out_trade_no,
              date: order.created_at,
              status: 'delivered', // 接口返回的都是已完成订单
              items: [{
                name: order.product_name,
                version: '', // API 中没有版本信息
                price: parseFloat(order.product_price_usd || '0'),
                quantity: 1,
                image: `/images/${productType}-main.jpg`,
                type: productType
              }],
              total: parseFloat(order.product_price_usd || '0'),
              paymentMethod: order.payment_channel,
              outTradeNo: order.out_trade_no,
              orderNo: order.orderNo,
              accountInfo: {
                name: order.account_name,
                password: order.account_password,
                emailPassword: order.email_password
              }
            };
          });
          
          console.log('转换后的订单数据:', transformedOrders);
          
          // 按时间从近到远排序
          const sortedOrders = transformedOrders.sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });
          
          setOrders(sortedOrders);
          setCurrentPage(1); // 重置到第一页
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error('获取订单历史失败:', err);
        setError(err.message || t('orderHistory.fetchOrdersFailed'));
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadOrderHistory();
  }, [isLoggedIn, t]);

  // 页码改变时滚动到顶部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // 分页逻辑
  const totalPages = Math.ceil(orders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = orders.slice(startIndex, endIndex);
  
  // 页码生成函数
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    const halfPages = Math.floor(maxPagesToShow / 2);
    
    let startPage = Math.max(1, currentPage - halfPages);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  // 获取状态显示信息
  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { text: t('orderHistory.status.pending'), icon: Clock, bgColor: 'bg-purple-50', textColor: 'text-purple-600', borderColor: 'border-purple-200' },
      processing: { text: t('orderHistory.status.processing'), icon: RefreshCw, bgColor: 'bg-blue-50', textColor: 'text-blue-600', borderColor: 'border-blue-200' },
      shipped: { text: t('orderHistory.status.shipped'), icon: Package, bgColor: 'bg-purple-50', textColor: 'text-purple-600', borderColor: 'border-purple-200' },
      delivered: { text: t('orderHistory.status.delivered'), icon: CheckCircle, bgColor: 'bg-green-50', textColor: 'text-green-600', borderColor: 'border-green-200' },
      cancelled: { text: t('orderHistory.status.cancelled'), icon: XCircle, bgColor: 'bg-red-50', textColor: 'text-red-600', borderColor: 'border-red-200' }
    };
    return statusMap[status] || statusMap.pending;
  };

  // 复制功能
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };

  // 处理关闭登录弹窗（返回首页）
  const handleCloseModal = () => {
    setShowLoginModal(false);
    navigate('/');
  };

  // 如果用户未登录，显示背景和登录弹窗
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoginModal 
          isOpen={showLoginModal}
          onClose={handleCloseModal}
          onLogin={handleLoginSuccess}
        />
      </div>
    );
  }

  // 如果正在加载
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('orderHistory.loading')}</p>
        </div>
      </div>
    );
  }

  // 如果有错误
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('orderHistory.loadFailed')}</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="inline-flex items-center bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            {t('orderHistory.reload')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('orderHistory.pageTitle')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('orderHistory.pageSubtitle')}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('orderHistory.totalOrders', { count: orders.length })}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 主要内容区域 */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 订单列表 */}
          <div className="space-y-6">
            {currentOrders.length > 0 ? (
              currentOrders.map((order, index) => {
                const statusInfo = getStatusInfo(order.status);
                const StatusIcon = statusInfo.icon;
                const globalIndex = startIndex + index; // 计算全局索引用于颜色
                
                return (
                  <div key={order.id} className={`bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden ${globalIndex % 2 === 0 ? 'border-l-4 border-purple-400' : 'border-l-4 border-blue-400'}`}>
                    
                    {/* 订单头部 */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-500">{t('orderHistory.orderNumber')}</span>
                            <span className="text-sm font-mono text-gray-900">{order.outTradeNo}</span>
                            <button
                              onClick={() => copyToClipboard(order.outTradeNo, `order-${order.outTradeNo}`)}
                              className="p-1 text-gray-400 hover:text-purple-500 hover:bg-purple-50 rounded transition-all"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            {copySuccess === `order-${order.outTradeNo}` && (
                              <span className="text-xs text-green-600 animate-fade-in">{t('orderHistory.copied')}</span>
                            )}
                          </div>
                          {order.orderNo && (
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-500">{t('orderHistory.merchantOrderNumber')}</span>
                              <span className="text-sm font-mono text-gray-700">{order.orderNo}</span>
                              <button
                                onClick={() => copyToClipboard(order.orderNo, `merchant-${order.orderNo}`)}
                                className="p-1 text-gray-400 hover:text-purple-500 hover:bg-purple-50 rounded transition-all"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              {copySuccess === `merchant-${order.orderNo}` && (
                                <span className="text-xs text-green-600 animate-fade-in">{t('orderHistory.copied')}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.textColor} ${statusInfo.borderColor} border`}>
                            <StatusIcon className="w-4 h-4 mr-1.5" />
                            {statusInfo.text}
                          </span>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">{t('orderHistory.orderAmount')}</p>
                            <p className="text-lg font-bold text-gray-900">${order.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 订单内容 */}
                    <div className="px-6 py-4">
                      {/* 基本信息 */}
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(order.date).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                        <span>{t('orderHistory.paymentMethod')}: {order.paymentMethod}</span>
                      </div>

                      {/* 商品列表 */}
                      <div className="space-y-3 mb-4">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  console.error('图片加载失败:', e.target.src);
                                  e.target.src = '/images/gpt-main.jpg'; // 使用gpt-main.jpg作为默认图片
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate">{item.name}</h4>
                              <p className="text-xs text-gray-500">{item.version} × {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 操作区域 */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <Link 
                          to={`${isSidebarVersion ? '/payment-success-sidebar' : '/detail/success'}?orderId=${order.outTradeNo}`} 
                          className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center"
                        >
                          {t('orderHistory.viewDetails')}
                          <ChevronRight className="w-4 h-4 ml-0.5" />
                        </Link>
                        
                        <div className="flex items-center space-x-2">
                          {order.status === 'pending' && (
                            <button className="bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors">
                              {t('orderHistory.payNow')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : orders.length === 0 ? (
              // 空状态
              <div className="bg-white rounded-2xl shadow-sm py-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShoppingBag className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('orderHistory.noOrders')}</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">{t('orderHistory.noOrdersDesc')}</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center bg-purple-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 transition-colors"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  {t('orderHistory.startShopping')}
                </Link>
              </div>
            ) : null}
          </div>

          {/* 分页组件 */}
          {orders.length > itemsPerPage && (
            <div className="mt-8 flex items-center justify-center">
              <div className="flex items-center space-x-2">
                {/* 上一页按钮 */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </button>

                {/* 第一页和省略号 */}
                {getPageNumbers()[0] > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      1
                    </button>
                    {getPageNumbers()[0] > 2 && (
                      <span className="px-2 text-gray-500">...</span>
                    )}
                  </>
                )}

                {/* 页码按钮 */}
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-purple-500 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {/* 最后一页和省略号 */}
                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                  <>
                    {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
                      <span className="px-2 text-gray-500">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                {/* 下一页按钮 */}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* 显示当前页信息 */}
              <div className="ml-6 text-sm text-gray-600">
                {t('orderHistory.page', { current: currentPage, total: totalPages, count: orders.length })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Toast 通知 */}
      {copySuccess && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium">{t('orderHistory.copySuccess')}</span>
          </div>
        </div>
      )}
    </div>
  );
} 